// Top-level app shell. Single-route v1: the map.
// /contribute and /admin lazy-load when needed.
//
// Multilingual routing — i18n initialises in main.tsx before the App
// mounts, so `i18n.language` is already set by the time we render. We
// scope wouter's <Router base> by the URL's language prefix (/es, /fr,
// etc.) so every route inside the SPA matches both bare English paths
// and prefixed locale paths transparently.
import { lazy, Suspense } from 'react';
import { Route, Router, Switch } from 'wouter';
import { MapView } from './routes/map';
import { langFromPath } from './i18n';

const Contribute = lazy(() => import('./routes/contribute').then(m => ({ default: m.Contribute })));
const Admin = lazy(() => import('./routes/admin').then(m => ({ default: m.Admin })));
const PathogenPage = lazy(() => import('./routes/pathogen').then(m => ({ default: m.PathogenPage })));
const HistoryPage = lazy(() => import('./routes/history').then(m => ({ default: m.HistoryPage })));
const EventPage = lazy(() => import('./routes/event').then(m => ({ default: m.EventPage })));
const AboutPage = lazy(() => import('./routes/about').then(m => ({ default: m.AboutPage })));
const FaqPage = lazy(() => import('./routes/faq').then(m => ({ default: m.FaqPage })));
const DisclaimerPage = lazy(() => import('./routes/disclaimer').then(m => ({ default: m.DisclaimerPage })));

export function App() {
  // English lives at the root (/); other locales get a /{code} prefix.
  // base is empty for English so wouter routes match `/`, `/event/:id`
  // unchanged. For Spanish base becomes `/es`, so the same routes match
  // `/es/`, `/es/event/:id`, etc.
  const langPrefix = langFromPath();
  const base = langPrefix && langPrefix !== 'en' ? `/${langPrefix}` : '';
  return (
    <Router base={base}>
    <Switch>
      <Route path="/" component={MapView} />
      <Route path="/contribute">
        <Suspense fallback={<Loading label="contribute" />}>
          <Contribute />
        </Suspense>
      </Route>
      <Route path="/admin">
        <Suspense fallback={<Loading label="admin" />}>
          <Admin />
        </Suspense>
      </Route>
      <Route path="/pathogen/:id">
        <Suspense fallback={<Loading label="pathogen" />}>
          <PathogenPage />
        </Suspense>
      </Route>
      <Route path="/history">
        <Suspense fallback={<Loading label="history" />}>
          <HistoryPage />
        </Suspense>
      </Route>
      <Route path="/event/:id">
        <Suspense fallback={<Loading label="event" />}>
          <EventPage />
        </Suspense>
      </Route>
      <Route path="/about">
        <Suspense fallback={<Loading label="about" />}>
          <AboutPage />
        </Suspense>
      </Route>
      <Route path="/methodology">
        <Suspense fallback={<Loading label="about" />}>
          <AboutPage />
        </Suspense>
      </Route>
      <Route path="/faq">
        <Suspense fallback={<Loading label="faq" />}>
          <FaqPage />
        </Suspense>
      </Route>
      <Route path="/disclaimer">
        <Suspense fallback={<Loading label="disclaimer" />}>
          <DisclaimerPage />
        </Suspense>
      </Route>
      <Route>
        <NotFound />
      </Route>
    </Switch>
    </Router>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <div className="flex h-screen items-center justify-center font-mono text-xs uppercase tracking-widest text-zinc-500">
      loading {label}…
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 bg-zinc-950 font-mono text-zinc-400">
      <span className="text-xs uppercase tracking-widest text-zinc-600">404</span>
      <a href="/" className="text-zinc-100 underline-offset-4 hover:underline">return to map</a>
    </div>
  );
}
